<template>
  <base-list-view
    @state-scroll="scrollList"
  >
    <template v-slot:filter-slot>
      <div class="participant-filter-line catalog-search-line">
        <button
          v-if="filters.length"
          @click="showFilter"
          :class="{ 'is-applied': isFiltered }"
          class="up-btn-line-icon"
          :aria-label="$t('shared.filters.btn_open')"
        >
          <i class="exu-filter-funnel-02"/>
        </button>
        <up-input-filter
          :startValue="searchInput"
          @onSearch="(e) => startSearch(e)"
        />
      </div>
      <nav-btn-tab
        v-if="isAuth"
        :id-nav-tab="'participants-relation-tab'"
        :tabs="relationTabs"
        :cur-val="activeTab"
        @change-value="changeTab"
      />
    </template>
    <template
      v-if="isShowFilter"
      v-slot:aside-filter-slot
    >
      <filters-view
        :filters="filters"
        :selectedFilters="filtersState"
        @update="applyFilters"
      />
    </template>
    <div class="participant-list">
      <pre-loader v-if="isLoadingPrev"/>
      <div
        v-for="participant in participantsList"
        :key="participant.id"
        :id="'participant-' + participant.id"
        class="participant-item"
        @click="saveScrollContext(participant)"
        @keydown.enter="saveScrollContext(participant)"
      >
        <participant-card
          :id="participant.id"
          :photo="participant.photo"
          :name="participant.name"
          :company="participant.company"
          :position="participant.position"
          :description="participant.description"
          :last-visit="participant.last_visit"
          :full-prop="participant"
          :markable="isAuth"
          :marked="participant.is_favorite"
          @marking="toggleFavorite"
        />
      </div>
      <system-card
        v-if="systemMessage"
        :system-message="systemMessage"
        :cbFunc="repeatGetPage"
      />
      <pre-loader v-if="isLoading && !isLoadingPrev"/>
    </div>
  </base-list-view>
</template>
<script>
import { DateTime } from 'luxon';
import appBaseNav from '@/components/mixins/appBaseNav';
import participants from '@/components/mixins/methodsParticipants';
import filters from '@/components/mixins/api/apiMethodsCatalogFilters';
import favorite, { FAVORITE_API_READY } from '@/components/mixins/api/apiMethodsParticipantFavorite';
import banners from '@/components/mixins/methodsBanners';
import listScrollMixin from '@/components/mixins/viewers/listScrollMixin';
import demoCatalog, {
  FILTER_API_READY, TAB_ALL, TAB_FAVORITE, TAB_FAVORITED_BY,
} from '@/components/mixins/demo/demoParticipantCatalog';

import baseListView from '@/components/shared/baseListView.vue';
import SystemCard from '@/components/shared/upCard/SystemCard.vue';
import FiltersView from '@/components/shared/asideMenu/asideFilters.vue';

export default {
  name: 'ListParticipantsView',
  components: { baseListView, SystemCard, FiltersView },
  props: {
    launchDownload: {
      type: Boolean,
    },
    locale: {
      type: String,
    },
    page: {
      type: String,
      default: null,
    },
  },
  data() {
    const savedRaw = sessionStorage.getItem('participantScrollCtx');
    const saved = savedRaw ? JSON.parse(savedRaw) : null;
    if (saved) sessionStorage.removeItem('participantScrollCtx');
    const startPage = saved ? saved.firstPage : (parseInt(this.page, 10) || 1);
    return {
      participantsList: [],
      filters: [],
      isLoading: true,
      firstPage: startPage,
      curPage: startPage,
      countPage: null,
      searchInput: saved?.search || null,
      activeTab: saved?.tab || TAB_ALL,

      isFiltered: !!saved?.isFiltered,
      filtersState: saved?.filtersState || {},
      filterValues: saved?.filterValues || {},
      filterParams: saved?.filterParams || null,

      isShowFilter: false,
      isLoadingPrev: false,
      isLastDownloaded: false,
      systemMessage: null,
      scrollTargetId: saved ? saved.targetId : null,
    };
  },
  mixins: [appBaseNav, participants, banners, filters, favorite, listScrollMixin, demoCatalog],
  watch: {
    locale() {
      this.$store.commit('setPageTitle', this.$t('programList.header_title'));
    },
  },
  computed: {
    selectedCategories() {
      return this.filterValues.category || [];
    },
    isAuth() {
      return !!this.$store.getters.getToken;
    },
    relationTabs() {
      return [
        { name: this.$t('participantList.tab_all'), value: TAB_ALL },
        { name: this.$t('participantList.tab_favorite'), value: TAB_FAVORITE },
        { name: this.$t('participantList.tab_favorited_by'), value: TAB_FAVORITED_BY },
      ];
    },
    // всё, что уходит в запрос списка помимо страницы и поиска
    requestParams() {
      const params = { ...(this.filterParams || {}) };
      if (this.activeTab !== TAB_ALL) params.relation = this.activeTab;
      return Object.keys(params).length ? params : null;
    },
  },
  mounted() {
    this.$store.commit('setPageTitle', this.$t('participantList.header_title'));

    Promise.all([
      this.getBanner('participant', this.$store.getters.getBannerParticipantTs),
      // конфиг фильтров не должен ронять каталог: не отдался — работаем без него
      this.getFilters('participant').catch(() => []),
    ])
      .then(([
        banner,
        filter,
      ]) => {
        this.isLoading = false;
        if (banner && Object.keys(banner).length) {
          this.$emit('showBanner', banner);
          const nowMilis = DateTime.now().toMillis();
          localStorage.setItem('bannerParticipantTs', nowMilis);
          this.$store.commit('setBannerParticipantTs', nowMilis);
        }

        if (Array.isArray(filter)) {
          this.filters = [...filter];
        }

        this.getPage();
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error);
      })
      .finally(() => {
      });
  },
  methods: {
    // Фильтр и вкладку берём из состояния, а не из аргумента: следующую
    // страницу listScrollMixin запрашивает через getPage() без параметров.
    getPage() {
      if (this.countPage === null || this.curPage <= this.countPage) {
        this.isLoading = true;

        this.getParticipantList(this.curPage, this.searchInput, this.requestParams)
          .then((data) => {
            this.isLoading = false;
            this.$emit('turnOff', {
              stateDownload: false,
              stateCheckInbox: false,
            });
            if (data?.results && Array.isArray(data.results)) {
              const tagged = data.results.map((p) => ({ ...p, sourcePage: data.page }));
              this.participantsList = [...this.participantsList, ...this.prepareList(tagged)];
              this.countPage = data.num_pages;
              this.curPage = data.page;
              this.isLastDownloaded = true;
              this.systemMessage = null;
              this.$nextTick(() => {
                const hadTarget = !!this.scrollTargetId;
                this.tryScrollToTarget();
                if (!hadTarget) this.checkScrollBoundaries();
              });
            } else {
              this.isLastDownloaded = false;
              this.systemMessage = this.$t('system.bad_connect');
            }
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error('getPage()::', error);
          });
      }
    },
    getPrevPage() {
      if (this.firstPage <= 1) return;

      const scrollEl = document.getElementById('content__wrapper');
      const listEl = document.getElementById('listView');

      const heightBeforeLoader = listEl.offsetHeight;
      const scrollBeforeLoader = scrollEl.scrollTop;

      this.isLoading = true;
      this.isLoadingPrev = true;

      this.$nextTick(() => {
        this.adjustScroll(scrollBeforeLoader, heightBeforeLoader);
      });

      this.getParticipantList(this.firstPage - 1, this.searchInput, this.requestParams)
        .then((data) => {
          if (data?.results && Array.isArray(data.results)) {
            const heightBeforeData = listEl.offsetHeight;
            const scrollBeforeData = scrollEl.scrollTop;

            this.isLoading = false;
            this.isLoadingPrev = false;
            const tagged = data.results.map((p) => ({ ...p, sourcePage: data.page }));
            this.participantsList = [...this.prepareList(tagged), ...this.participantsList];
            this.firstPage -= 1;
            this.systemMessage = null;

            this.$nextTick(() => {
              this.adjustScroll(scrollBeforeData, heightBeforeData);
            });
          } else {
            this.isLoading = false;
            this.isLoadingPrev = false;
            this.systemMessage = this.$t('system.bad_connect');
          }
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('getPrevPage()::', error);
          this.isLoading = false;
          this.isLoadingPrev = false;
        });
    },
    // Оба флага сняты — отбор по фильтру и по вкладке делает сервер, страница
    // возвращается как есть. Ветки ниже остались на случай, если флаг придётся
    // вернуть; вместе с ними уйдёт и эмулятор каталога.
    prepareList(list) {
      let result = list;

      if (!FAVORITE_API_READY) {
        result = this.demoApplyFavoriteFields(result);
        result = this.demoFilterByRelation(result, this.activeTab);
      }

      if (!FILTER_API_READY) {
        result = this.demoAssignCategories(result, this.filters);
        result = this.demoFilterByCategory(result, this.selectedCategories);
      }

      return result;
    },
    changeTab(value) {
      if (value === this.activeTab) return;
      this.activeTab = value;
      this.resetList();
      this.getPage();
    },
    showFilter() {
      this.isShowFilter = !this.isShowFilter;
    },
    toggleFavorite(id) {
      const participant = this.participantsList.find((item) => item.id === id);
      if (!participant) return;

      const wanted = !participant.is_favorite;
      // Отметку показываем сразу: ждать ответа сервера ради галочки незачем.
      // Карточку с вкладки «Избранное» при снятии не убираем — она уходит со
      // следующей загрузкой списка. Иначе список дёргается под пальцем, а
      // случайно снятую отметку вернуть уже некуда.
      participant.is_favorite = wanted;

      this.setFavorite(id, wanted)
        .catch((error) => {
          // не сохранилось — возвращаем как было, иначе список врёт
          participant.is_favorite = !wanted;
          // eslint-disable-next-line no-console
          console.error('toggleFavorite()::', error);
        });
    },
    // Панель ведает только фильтрами: строку поиска над списком она не
    // трогает, поиск чистится крестиком в самом поле.
    applyFilters(params) {
      this.isShowFilter = false;

      if (params !== null && params.doFilter) {
        this.isFiltered = !!Object.keys(params.filters).length;
        this.filtersState = params.filtersState;
        this.filterValues = params.filters;
        this.filterParams = this.isFiltered
          ? { filter: JSON.stringify(params.filters) }
          : null;
        this.resetList();
        this.getPage();
      } else if (this.isFiltered) {
        this.isFiltered = false;
        this.filtersState = {};
        this.filterValues = {};
        this.filterParams = null;
        this.resetList();
        // вкладка фильтром не сбрасывается — она остаётся выбранной
        this.getPage();
      }
    },
    resetList() {
      this.firstPage = 1;
      this.curPage = 1;
      this.countPage = null;
      this.participantsList = [];
    },
    // Возврат из профиля должен поднять тот же список, а не «Все» с первой
    // страницы: вместе с позицией запоминаем поиск и фильтры.
    saveScrollContext(participant) {
      sessionStorage.setItem('participantScrollCtx', JSON.stringify({
        targetId: `participant-${participant.id}`,
        firstPage: participant.sourcePage || 1,
        search: this.searchInput,
        tab: this.activeTab,
        isFiltered: this.isFiltered,
        filtersState: this.filtersState,
        filterValues: this.filterValues,
        filterParams: this.filterParams,
      }));
    },
    tryScrollToTarget() {
      if (!this.scrollTargetId) return;
      const el = document.getElementById(this.scrollTargetId);
      if (el) {
        el.scrollIntoView({ block: 'center' });
        this.scrollTargetId = null;
      }
    },
    startSearch(data) {
      this.searchInput = data;
      this.resetList();
      this.getPage();
    },
  },
};
</script>
<style lang="scss">
@import '@/assets/style/variables.scss';

// Строка поиска и лента вкладок — одна карточка: у `navtab-line__wrapper`
// белый фон и скругления по :first-child/:last-child, он рассчитан стоять
// в стопке. Прозрачная строка поиска оставляла эту карточку без верха.
// Сама строка (сцепка воронки, поля и лупы) описана в upInputFilter.
.participant-filter-line {
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  padding: 8px 8px 4px;
  background: $color-base;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}
.participant {
  &-list {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }
  &-list &-item {
    flex: 1 1 140px;
    max-width: calc(50% - 6px);
    @media (min-width: 480px) {
      flex: 1 1 180px;
      max-width: calc(33.333% - 6px);
    }

    & .upCard {
      border-radius: 16px;
    }
  }
}
</style>
